// Chemin: C:\gestion-copro\src\types\affaire.types.ts
// Session 66 : Ajout type "audit" pour investigations préventives
// Session 129 : Ajout courrierId dans EvenementAffaire pour chronologie courriers

import { Timestamp } from 'firebase/firestore';

/**
 * Types d'affaires possibles
 */
export type TypeAffaire = 
  | 'litige_construction'  // Malfaçons, garanties
  | 'sinistre'             // Dégât des eaux, incendie, vandalisme
  | 'travaux'              // Ravalement, ascenseur, toiture
  | 'contentieux'          // Impayés, non-respect règlement
  | 'projet'               // Fibre, changement syndic, digicode
  | 'ag'                   // Assemblée générale
  | 'audit'                // Vérification, investigation préventive, contrôle conformité
  | 'autre';

/**
 * Statuts du cycle de vie d'une affaire
 */
export type StatutAffaire = 
  | 'ouverte'      // Nouvelle affaire, à traiter
  | 'en_cours'     // Traitement actif
  | 'en_attente'   // Bloquée (attente réponse, expertise, etc.)
  | 'cloturee';    // Terminée

/**
 * Niveaux de priorité
 */
export type PrioriteAffaire = 'basse' | 'normale' | 'haute' | 'urgente';

/**
 * Garanties applicables (pour litiges construction)
 */
export type GarantieApplicable = 'gpa' | 'biennale' | 'decennale' | 'aucune';

/**
 * Événement dans la timeline d'une affaire
 */
export interface EvenementAffaire {
  id: string;
  date: Timestamp;
  type: 'creation' | 'signalement' | 'document' | 'courrier' | 'reunion' | 'decision' | 'note' | 'cloture';
  titre: string;
  description?: string;
  auteurId?: string;
  auteurNom?: string;
  // Références vers les entités liées
  signalementId?: string;
  documentId?: string;
  courrierId?: string; // Session 129 : traçabilité courriers dans la chronologie
}

/**
 * Entité principale Affaire
 */
export interface Affaire {
  id: string;
  coproprieteId: string;
  
  // Identification
  numero: string;           // Format: AFF-2026-001
  titre: string;            // Ex: "Infiltrations toiture bâtiment A"
  type: TypeAffaire;
  
  // État
  statut: StatutAffaire;
  priorite: PrioriteAffaire;
  
  // Description
  description: string;
  
  // Dates
  dateOuverture: Timestamp;
  dateCloture?: Timestamp;
  
  // Relations
  interlocuteurIds: string[];       // Interlocuteurs concernés
  signalementIds: string[];         // Signalements rattachés
  documentIds: string[];            // Documents rattachés
  
  // Spécifique litiges construction
  garantieApplicable?: GarantieApplicable;
  dateFinGarantie?: Timestamp;      // Calculée ou saisie
  
  // Financier
  montantEstime?: number;           // Estimation coût/enjeu
  montantFinal?: number;            // Montant final si connu
  
  // Timeline (événements manuels uniquement, les autres sont générés)
  evenements: EvenementAffaire[];
  
  // Session 179 : Résolution transversale
  /**
   * Marque le dossier comme résolu (problème traité, même si pas formellement clôturé).
   * Un dossier en_cours peut être résolu si l'utilisateur considère le sujet traité.
   * Les éléments resolu=true sont exclus du contexte SENTINELLE.
   * Rétrocompatible : absent ou false = non résolu.
   */
  resolu?: boolean;
  
  // Métadonnées
  createdAt: Timestamp;
  updatedAt: Timestamp;
  createdBy: string;
  closedBy?: string;
}

/**
 * Données pour créer une nouvelle affaire
 */
export interface CreateAffaireData {
  coproprieteId: string;
  titre: string;
  type: TypeAffaire;
  description: string;
  priorite?: PrioriteAffaire;
  interlocuteurIds?: string[];
  garantieApplicable?: GarantieApplicable;
  montantEstime?: number;
}

/**
 * Données pour mettre à jour une affaire
 */
export interface UpdateAffaireData {
  titre?: string;
  type?: TypeAffaire;
  statut?: StatutAffaire;
  priorite?: PrioriteAffaire;
  description?: string;
  interlocuteurIds?: string[];
  garantieApplicable?: GarantieApplicable;
  montantEstime?: number;
  montantFinal?: number;
}

/**
 * Labels pour affichage
 */
export const TYPE_AFFAIRE_LABELS: Record<TypeAffaire, string> = {
  litige_construction: 'Litige construction',
  sinistre: 'Sinistre',
  travaux: 'Travaux',
  contentieux: 'Contentieux',
  projet: 'Projet',
  ag: 'Assemblée générale',
  audit: 'Audit / Vérification',
  autre: 'Autre'
};

export const STATUT_AFFAIRE_LABELS: Record<StatutAffaire, string> = {
  ouverte: 'Ouverte',
  en_cours: 'En cours',
  en_attente: 'En attente',
  cloturee: 'Clôturée'
};

export const PRIORITE_AFFAIRE_LABELS: Record<PrioriteAffaire, string> = {
  basse: 'Basse',
  normale: 'Normale',
  haute: 'Haute',
  urgente: 'Urgente'
};

export const GARANTIE_LABELS: Record<GarantieApplicable, string> = {
  gpa: 'Garantie Parfait Achèvement (1 an)',
  biennale: 'Garantie Biennale (2 ans)',
  decennale: 'Garantie Décennale (10 ans)',
  aucune: 'Aucune garantie applicable'
};

/**
 * Couleurs pour les statuts (classes Tailwind)
 */
export const STATUT_AFFAIRE_COLORS: Record<StatutAffaire, string> = {
  ouverte: 'bg-blue-100 text-blue-800',
  en_cours: 'bg-yellow-100 text-yellow-800',
  en_attente: 'bg-gray-100 text-gray-800',
  cloturee: 'bg-green-100 text-green-800'
};

/**
 * Couleurs pour les priorités (classes Tailwind)
 */
export const PRIORITE_AFFAIRE_COLORS: Record<PrioriteAffaire, string> = {
  basse: 'bg-gray-100 text-gray-600',
  normale: 'bg-blue-100 text-blue-700',
  haute: 'bg-orange-100 text-orange-700',
  urgente: 'bg-red-100 text-red-700'
};

/**
 * Couleurs pour les types d'affaires (classes Tailwind)
 */
export const TYPE_AFFAIRE_COLORS: Record<TypeAffaire, string> = {
  litige_construction: 'bg-red-100 text-red-800',
  sinistre: 'bg-orange-100 text-orange-800',
  travaux: 'bg-yellow-100 text-yellow-800',
  contentieux: 'bg-purple-100 text-purple-800',
  projet: 'bg-blue-100 text-blue-800',
  ag: 'bg-indigo-100 text-indigo-800',
  audit: 'bg-cyan-100 text-cyan-800',
  autre: 'bg-gray-100 text-gray-800'
};
